import { fetchVersion } from '../Components/Login/FlashFiles'

// Get version data from session storage or fetch it
export const getDevicePrefix = (deviceName) => {
    const prefixMap = {
        playcomputer: 'PC',
        zing: 'ZG',
        crawlee: 'CW',
        roboki: 'RB',
        klaw: 'KW',
        atr_h2: 'AT',
        toki: 'TK',
        poki: 'PK',
        moki: 'MK',
    }
    return prefixMap[deviceName?.toLowerCase()] || ''
}

// Check if firmware update is available
export const checkFirmwareVersion = async (
    deviceName,
    currentVersion,
    branchOverride
) => {
    try {
        // Get version data from session storage or fetch it
        let versionDataStr = sessionStorage.getItem('versionData')

        // If not in session storage, fetch it
        if (!versionDataStr) {
            console.log('No version data in session storage, fetching...')
            try {
                const versionData = await fetchVersion()
                if (versionData) {
                    sessionStorage.setItem(
                        'versionData',
                        JSON.stringify(versionData)
                    )
                    versionDataStr = JSON.stringify(versionData)
                } else {
                    console.log('Failed to fetch version data')
                    return { updateAvailable: false }
                }
            } catch (fetchError) {
                console.error('Error fetching version data:', fetchError)
                return { updateAvailable: false }
            }
        }

        const versionData = JSON.parse(versionDataStr)
        console.log('Version data loaded:', versionData)

        // Extract major.minor version from current version (e.g., "2.1.4" -> "2.1")
        const versionParts = currentVersion.split('.')
        const majorMinor =
            branchOverride || `${versionParts[0]}.${versionParts[1]}`

        console.log(
            'Looking for device:',
            deviceName,
            'version branch:',
            majorMinor
        )

        // Get latest version for this device and version branch
        const deviceData = versionData[deviceName.toLowerCase()]
        if (!deviceData) {
            console.log(`No version data found for device: ${deviceName}`)
            console.log('Available devices:', Object.keys(versionData))
            return { updateAvailable: false }
        }

        if (!deviceData[majorMinor]) {
            console.log(
                `No version data found for ${deviceName} version ${majorMinor}`
            )
            console.log('Available versions:', Object.keys(deviceData))
            return { updateAvailable: false }
        }

        const latestVersion = deviceData[majorMinor].version
        const firmwarePath = deviceData[majorMinor].path

        console.log('Firmware check:', {
            device: deviceName,
            current: currentVersion,
            latest: latestVersion,
            path: firmwarePath,
            majorMinor: majorMinor,
        })

        // Compare versions
        const updateAvailable =
            compareVersionStrings(latestVersion, currentVersion) > 0

        console.log('Update available:', updateAvailable)

        const prefix = getDevicePrefix(deviceName)

        return {
            updateAvailable,
            currentVersion,
            latestVersion,
            firmwarePath,
            deviceName,
            majorMinor,
            prefix,
        }
    } catch (error) {
        console.error('Error checking firmware version:', error)
        return { updateAvailable: false, error: error.message }
    }
}

// Compare version strings (e.g., "2.0.5.0" vs "2.0.3.0")
const compareVersionStrings = (v1, v2) => {
    const parts1 = v1.split('.').map(Number)
    const parts2 = v2.split('.').map(Number)

    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
        const num1 = parts1[i] || 0
        const num2 = parts2[i] || 0

        if (num1 > num2) return 1
        if (num1 < num2) return -1
    }

    return 0
}

// Get firmware download URL based on device and version
export const getFirmwareDownloadUrl = (deviceName, majorMinor) => {
    // Map device names to firmware download paths
    const deviceMap = {
        Playcomputer: `playcomputer-${majorMinor}`,
        Zing: `zing${majorMinor}`,
        Crawlee: `crawlee${majorMinor}`,
        Roboki: 'roboki',
        Klaw: 'klaw',
        Atr_h2: 'atr_h2',
        'AI-Module': 'pc_ai_acc',
        'AI-Webcam': 'ai-webcam',
        Port11: 'esp-mspm0-debugger',
    }

    const firmwareName = deviceMap[deviceName] || deviceName.toLowerCase()

    // Return URL to firmware update page (can be updated later)
    return `/firmware-update?device=${firmwareName}&version=${majorMinor}`
}

const API_BASE = 'https://embedded.plode.org/v2'

const normalizeFirmwarePath = (rawPath) => {
    if (!rawPath) return null
    const idx = rawPath.indexOf('/bins/')
    if (idx !== -1) return rawPath.substring(idx)
    if (rawPath.startsWith('/bins/')) return rawPath
    return '/bins/production/' + rawPath
}

export const fetchFirmwareFiles = async (firmwareCheck) => {
    const basePath = normalizeFirmwarePath(firmwareCheck.firmwarePath)
    if (!basePath) throw new Error('Invalid firmware path')

    const changelogRes = await fetch(`${API_BASE}${basePath}/changelog.json`)
    if (!changelogRes.ok)
        throw new Error('Failed to fetch firmware changelog')

    const changelog = await changelogRes.json()
    const versions = Object.keys(changelog)

    if (versions.length === 0)
        throw new Error('No firmware versions found in changelog')

    let targetVersion = null

    if (firmwareCheck.latestVersion) {
        targetVersion = versions.find(
            (v) => v === firmwareCheck.latestVersion
        )
        if (!targetVersion) {
            const prefix = firmwareCheck.prefix || getDevicePrefix(firmwareCheck.deviceName)
            targetVersion = versions.find(
                (v) => v === `${prefix}${firmwareCheck.latestVersion}`
            )
        }
    }

    if (!targetVersion) {
        targetVersion = versions.sort((a, b) => {
            const na = a.replace(/^[a-zA-Z_]+/, '').split('.').map(Number)
            const nb = b.replace(/^[a-zA-Z_]+/, '').split('.').map(Number)
            for (let i = 0; i < Math.max(na.length, nb.length); i++) {
                if ((nb[i] || 0) !== (na[i] || 0))
                    return (nb[i] || 0) - (na[i] || 0)
            }
            return 0
        })[0]
    }

    const entry = changelog[targetVersion]
    const locationMap = entry?.location || entry?.locations || {}

    if (Object.keys(locationMap).length === 0)
        throw new Error('No firmware file locations found')

    const firmwareFolderPath = `${basePath}/${targetVersion}`
    const filesRes = await fetch(`${API_BASE}/filenames`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: firmwareFolderPath }),
    })
    if (!filesRes.ok)
        throw new Error(`Failed to fetch firmware filenames: ${filesRes.statusText}`)

    const filesPayload = await filesRes.json()
    const filesList = filesPayload?.files || []

    const flashFilesData = []
    for (const [nameKey, addr] of Object.entries(locationMap)) {
        const actualName = filesList.find((f) => f.includes(nameKey))
        if (actualName) {
            flashFilesData.push({
                fileName: actualName,
                address: parseInt(addr),
                data: null,
            })
        }
    }

    if (flashFilesData.length === 0)
        throw new Error('No matching firmware files found')

    for (const file of flashFilesData) {
        const fileRes = await fetch(
            `${API_BASE}${firmwareFolderPath}/${file.fileName}`
        )
        if (!fileRes.ok)
            throw new Error(`Download failed for ${file.fileName}: ${fileRes.statusText}`)
        const blob = await fileRes.blob()
        const reader = new FileReader()
        file.data = await new Promise((resolve, reject) => {
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = (e) => reject(e)
            reader.readAsBinaryString(blob)
        })
    }

    return flashFilesData
}

// Check if PLODE Web Agent is running
export const checkPlodeWebAgent = (socketData) => {
    return socketData && socketData.socket && socketData.socket.connected
}

// Check if USB mount point exists
export const checkUSBMountPoint = async (socket) => {
    try {
        if (!socket) return false

        // Use existing getMountPoint function
        return new Promise((resolve) => {
            socket.emit('get-mount-point', {}, (response) => {
                resolve(response.success && response.mountPoint)
            })

            setTimeout(() => resolve(false), 5000)
        })
    } catch (error) {
        console.error('Error checking USB mount point:', error)
        return false
    }
}

export default {
    checkFirmwareVersion,
    getFirmwareDownloadUrl,
    checkPlodeWebAgent,
    checkUSBMountPoint,
    fetchFirmwareFiles,
}
